import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, BookMarked, LayoutTemplate, FileText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService, smartResult, breakEven } from "@/lib/calc";
import { SectionCard, SectionTitle, InfoBox, InfoTooltip, PillGroup, ResultRow } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { WhatIf } from "@/components/WhatIf";
import { TEMPLATES } from "@/lib/templates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-3 focus-visible:ring-2 focus-visible:ring-brand-champagne";

function emptyMaterial() {
  return { id: crypto.randomUUID(), material_id: null, name: "", method: "detailed", package_price: "", package_quantity: "", unit: "ml", quantity_used: "", direct_cost: "" };
}

export default function ServiceEditor() {
  const { t, user, fmt } = useAuth();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ["service", id],
    queryFn: () => api.get(`/services/${id}`).then((r) => r.data),
    enabled: !isNew,
  });
  const { data: costs = [] } = useQuery({ queryKey: ["fixed-costs"], queryFn: () => api.get("/fixed-costs").then((r) => r.data) });
  const { data: library = [] } = useQuery({ queryKey: ["materials"], queryFn: () => api.get("/materials").then((r) => r.data) });

  const [form, setForm] = useState({
    name: "", description: "", duration_min: "", extra_time_pct: 15, desired_margin: 30, current_price: "", materials: [],
  });
  const [saving, setSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(isNew);

  const applyTemplate = (tpl) => {
    setForm((f) => ({
      ...f,
      name: f.name || t(`templates.name_${tpl.id}`),
      materials: tpl.rows.map((r) => ({ ...emptyMaterial(), name: t(`templates.row_${r}`) })),
    }));
    setShowTemplates(false);
  };

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name, description: existing.description || "",
        duration_min: existing.duration_min ?? "", extra_time_pct: existing.extra_time_pct ?? 15,
        desired_margin: existing.desired_margin ?? 30, current_price: existing.current_price ?? "",
        materials: (existing.materials || []).map((m) => ({ ...m })),
      });
    }
  }, [existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const serviceForCalc = useMemo(() => ({
    ...form,
    duration_min: Number(form.duration_min) || 0,
    extra_time_pct: Number(form.extra_time_pct) || 0,
    desired_margin: Number(form.desired_margin) || 0,
    current_price: form.current_price === "" ? null : Number(form.current_price),
    materials: form.materials,
  }), [form]);

  const calc = useMemo(() => computeService(serviceForCalc, user), [serviceForCalc, user]);
  const fixedTotal = costs.reduce((s, c) => s + Number(c.monthly_amount || 0), 0);
  const be = breakEven(calc, fixedTotal);
  const smart = smartResult(calc);

  // Materials handlers
  const updMaterial = (mid, patch) => set("materials", form.materials.map((m) => (m.id === mid ? { ...m, ...patch } : m)));
  const addMaterial = () => set("materials", [...form.materials, emptyMaterial()]);
  const removeMaterial = (mid) => set("materials", form.materials.filter((m) => m.id !== mid));
  const linkLibrary = (mid, v) => {
    if (v === "manual") { updMaterial(mid, { material_id: null }); return; }
    const lib = library.find((l) => l.id === v);
    if (lib) updMaterial(mid, { material_id: v, name: lib.name, method: "detailed", package_price: lib.package_price, package_quantity: lib.package_quantity, unit: lib.unit });
  };
  const saveToLibrary = async (m) => {
    try {
      const { data } = await api.post("/materials", { name: m.name, category: "other", package_price: Number(m.package_price) || 0, package_quantity: Number(m.package_quantity) || 1, unit: m.unit });
      updMaterial(m.id, { material_id: data.id });
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(t("materials.savedToLibrary"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("editor.name") + " — " + t("common.required")); return; }
    if (Number(form.desired_margin) >= 100) { toast.error(t("editor.marginTitle")); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), description: form.description,
      duration_min: Number(form.duration_min) || 0, extra_time_pct: Number(form.extra_time_pct) || 0,
      desired_margin: Number(form.desired_margin) || 0,
      current_price: form.current_price === "" ? null : Number(form.current_price),
      materials: form.materials.map((m) => ({
        id: m.id, material_id: m.material_id || null, name: m.name, method: m.method,
        package_price: Number(m.package_price) || 0, package_quantity: Number(m.package_quantity) || 1,
        unit: m.unit, quantity_used: Number(m.quantity_used) || 0, direct_cost: Number(m.direct_cost) || 0,
      })),
    };
    try {
      if (isNew) await api.post("/services", payload);
      else await api.put(`/services/${id}`, payload);
      qc.invalidateQueries({ queryKey: ["services"] });
      qc.invalidateQueries({ queryKey: ["service", id] });
      toast.success(t("editor.saved"));
      navigate("/services");
    } catch (err) {
      toast.error((err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const marginOptions = [20, 30, 40, 50].map((v) => ({ value: v, label: `${v}%` }));
  const extraOptions = [10, 15, 20].map((v) => ({ value: v, label: `${v}%` }));

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate("/services")} className="inline-flex items-center gap-2 text-sm text-brand-taupe hover:text-brand-anthracite" data-testid="editor-back">
          <ArrowLeft className="h-4 w-4" /> {t("common.back")}
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" aria-label={t("priceCard.create")} onClick={() => navigate(`/services/${id}/price-card`)} data-testid="editor-price-card" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand h-11">
              <FileText className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">{t("priceCard.create")}</span>
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="editor-save" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 h-11 px-5">
            <Save className="mr-2 h-4 w-4" /> {saving ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </div>

      <h1 className="font-serif text-3xl font-medium text-brand-anthracite">{isNew ? t("editor.newTitle") : t("editor.editTitle")}</h1>

      {isNew && showTemplates && (
        <SectionCard accent data-testid="template-picker">
          <SectionTitle sub={t("templates.chooseSubtitle")}>{t("templates.chooseTitle")}</SectionTitle>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowTemplates(false)} data-testid="template-scratch" className="rounded-xl border border-brand-line bg-white px-4 py-2 text-sm font-medium text-brand-anthracite hover:border-brand-taupe">
              {t("templates.fromScratch")}
            </button>
            {TEMPLATES.map((tpl) => (
              <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`template-${tpl.id}`}
                className="inline-flex items-center gap-2 rounded-xl border border-brand-champagne/50 bg-brand-champagne/15 px-4 py-2 text-sm font-medium text-brand-anthracite hover:bg-brand-champagne/25">
                <LayoutTemplate className="h-4 w-4" /> {t(`templates.name_${tpl.id}`)}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-brand-taupe">{t("templates.note")}</p>
        </SectionCard>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT: inputs */}
        <div className="lg:col-span-3 space-y-6">
          <SectionCard>
            <SectionTitle>{t("editor.basics")}</SectionTitle>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("editor.name")}</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("editor.namePlaceholder")} className={inputClass} data-testid="editor-name" />
              </div>
              <div className="space-y-2">
                <Label>{t("editor.description")} <span className="text-brand-taupe font-normal">({t("common.optional")})</span></Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("editor.descriptionPlaceholder")} className={inputClass} data-testid="editor-description" />
              </div>
              <div className="space-y-2">
                <Label>{t("editor.duration")}</Label>
                <p className="text-xs text-brand-taupe">{t("editor.durationHelp")}</p>
                <div className="relative max-w-[200px]">
                  <Input type="number" min="0" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={`${inputClass} pr-12`} data-testid="editor-duration" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-brand-taupe">{t("common.min")}</span>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle>{t("editor.extraTitle")}</SectionTitle>
            <InfoBox tone="blush">{t("editor.extraHelp")}</InfoBox>
            <GuidedTip id="hourly" className="mt-3">{t("tips.hourly")}</GuidedTip>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <PillGroup options={extraOptions} value={Number(form.extra_time_pct)} onChange={(v) => set("extra_time_pct", v)} testid="editor-extra-pill" />
              <div className="relative w-28">
                <Input type="number" min="0" value={form.extra_time_pct} onChange={(e) => set("extra_time_pct", e.target.value)} className={`${inputClass} pr-8`} data-testid="editor-extra-custom" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-brand-taupe">%</span>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-brand-sand/50 p-3"><div className="text-xs text-brand-taupe">{t("editor.treatmentTime")}</div><div className="font-semibold text-brand-anthracite">{calc.duration.toFixed(0)} {t("common.min")}</div></div>
              <div className="rounded-xl bg-brand-sand/50 p-3"><div className="text-xs text-brand-taupe">{t("editor.extraTime")}</div><div className="font-semibold text-brand-anthracite">{calc.extraMin.toFixed(0)} {t("common.min")}</div></div>
              <div className="rounded-xl bg-brand-champagne/20 p-3"><div className="text-xs text-brand-taupe">{t("editor.realTime")}</div><div className="font-semibold text-brand-anthracite">{calc.effectiveMin.toFixed(0)} {t("common.min")}</div></div>
            </div>
          </SectionCard>

          <SectionCard>
            <div className="flex items-center justify-between">
              <SectionTitle sub={t("editor.materialsHelp")}>{t("editor.materialsTitle")}</SectionTitle>
            </div>
            <div className="space-y-2 mb-4">
              <GuidedTip id="material-consumption">{t("tips.materialConsumption")}</GuidedTip>
              <GuidedTip id="direct-cost">{t("tips.directCost")}</GuidedTip>
            </div>
            <div className="space-y-4">
              {form.materials.length === 0 && <p className="text-sm text-brand-taupe">{t("editor.noMaterials")}</p>}
              {form.materials.map((m) => {
                const cost = m.method === "simple" ? (Number(m.direct_cost) || 0) : ((Number(m.package_quantity) || 0) > 0 ? (Number(m.package_price) || 0) / (Number(m.package_quantity)) * (Number(m.quantity_used) || 0) : 0);
                return (
                  <div key={m.id} className="rounded-xl border border-brand-line p-4 space-y-3" data-testid={`material-item-${m.id}`}>
                    <div className="flex items-center gap-2">
                      <Input value={m.name} onChange={(e) => updMaterial(m.id, { name: e.target.value })} placeholder={t("editor.productName")} className={`${inputClass} flex-1`} data-testid={`material-name-${m.id}`} />
                      <button onClick={() => removeMaterial(m.id)} className="rounded-lg p-2 text-brand-taupe hover:bg-[#B85B5B]/10 hover:text-[#B85B5B]" data-testid={`material-remove-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                    {library.length > 0 && (
                      <Select value={m.material_id || "manual"} onValueChange={(v) => linkLibrary(m.id, v)}>
                        <SelectTrigger className={`${inputClass} py-2`} data-testid={`material-library-${m.id}`}><SelectValue placeholder={t("materials.chooseFromLibrary")} /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="manual">{t("materials.manual")}</SelectItem>
                          {library.map((lib) => <SelectItem key={lib.id} value={lib.id}>{lib.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {m.material_id && <span className="inline-flex items-center gap-1 text-xs text-brand-champagne" data-testid={`material-linked-${m.id}`}><BookMarked className="h-3 w-3" />{t("materials.fromLibrary")}</span>}
                    <Tabs value={m.method} onValueChange={(v) => updMaterial(m.id, { method: v })}>
                      <TabsList className="bg-brand-sand/60 rounded-lg">
                        <TabsTrigger value="detailed" data-testid={`material-method-detailed-${m.id}`}>{t("editor.methodDetailed")}</TabsTrigger>
                        <TabsTrigger value="simple" data-testid={`material-method-simple-${m.id}`}>{t("editor.methodSimple")}</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {m.method === "detailed" ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><Label className="text-xs">{t("editor.packagePrice")}</Label><Input type="number" min="0" value={m.package_price} onChange={(e) => updMaterial(m.id, { package_price: e.target.value })} className={inputClass} data-testid={`material-pkgprice-${m.id}`} /></div>
                        <div className="space-y-1"><Label className="text-xs">{t("editor.packageQty")}</Label><Input type="number" min="0" value={m.package_quantity} onChange={(e) => updMaterial(m.id, { package_quantity: e.target.value })} className={inputClass} data-testid={`material-pkgqty-${m.id}`} /></div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t("editor.unit")}</Label>
                          <Select value={m.unit} onValueChange={(v) => updMaterial(m.id, { unit: v })}>
                            <SelectTrigger className={inputClass} data-testid={`material-unit-${m.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              {["ml", "g", "pieces", "custom"].map((u) => <SelectItem key={u} value={u}>{t(`editor.unit_${u}`)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">{t("editor.qtyUsed")}</Label><Input type="number" min="0" value={m.quantity_used} onChange={(e) => updMaterial(m.id, { quantity_used: e.target.value })} className={inputClass} data-testid={`material-qtyused-${m.id}`} /></div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Label className="text-xs">{t("editor.estimatedCost")}</Label>
                        <div className="relative max-w-[200px]">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe">{user.currency === "GBP" ? "£" : "€"}</span>
                          <Input type="number" min="0" step="0.1" value={m.direct_cost} onChange={(e) => updMaterial(m.id, { direct_cost: e.target.value })} className={`${inputClass} pl-8`} data-testid={`material-direct-${m.id}`} />
                        </div>
                      </div>
                    )}
                    <div className="text-sm text-brand-anthracite bg-brand-champagne/15 rounded-lg px-3 py-2" data-testid={`material-cost-${m.id}`}>
                      {t("editor.productCostMsg", { cost: fmt(cost) })}
                    </div>
                    {m.method === "detailed" && !m.material_id && (m.name || "").trim() && (
                      <button onClick={() => saveToLibrary(m)} className="text-xs font-medium text-brand-champagne hover:underline" data-testid={`material-save-lib-${m.id}`}>
                        + {t("materials.saveToLibrary")}
                      </button>
                    )}
                  </div>
                );
              })}
              <Button variant="outline" onClick={addMaterial} data-testid="editor-add-material" className="w-full rounded-xl border-brand-taupe/40 border-dashed text-brand-anthracite hover:bg-brand-sand">
                <Plus className="mr-2 h-4 w-4" /> {t("common.addProduct")}
              </Button>
            </div>
            <div className="mt-4"><InfoBox title={t("editor.disclaimerTitle")}>{t("editor.disclaimerBody")}</InfoBox></div>
          </SectionCard>

          <SectionCard>
            <div className="flex items-center gap-2 mb-1">
              <SectionTitle>{t("editor.marginTitle")}</SectionTitle>
              <div className="mb-4"><InfoTooltip text={t("editor.marginHelp")} /></div>
            </div>
            <GuidedTip id="margin" className="mb-4">{t("tips.margin")}</GuidedTip>
            <div className="flex flex-wrap items-center gap-3">
              <PillGroup options={marginOptions} value={Number(form.desired_margin)} onChange={(v) => set("desired_margin", v)} testid="editor-margin-pill" />
              <div className="relative w-28">
                <Input type="number" min="0" max="99" value={form.desired_margin} onChange={(e) => set("desired_margin", e.target.value)} className={`${inputClass} pr-8`} data-testid="editor-margin-custom" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-brand-taupe">%</span>
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle>{t("editor.currentTitle")}</SectionTitle>
            <div className="space-y-2 max-w-[240px]">
              <Label>{t("editor.currentPrice")}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe">{user.currency === "GBP" ? "£" : "€"}</span>
                <Input type="number" min="0" step="0.5" value={form.current_price} onChange={(e) => set("current_price", e.target.value)} placeholder={t("editor.currentPricePlaceholder")} className={`${inputClass} pl-8`} data-testid="editor-current-price" />
              </div>
            </div>
          </SectionCard>
        </div>

        {/* RIGHT: results (sticky on desktop) */}
        <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-6 lg:self-start">
          <SectionCard accent data-testid="editor-results">
            <SectionTitle>{t("editor.baseCost")}</SectionTitle>
            <ResultRow label={t("editor.totalMaterial")} value={fmt(calc.totalMaterial)} />
            <ResultRow label={t("editor.timeCost")} value={fmt(calc.timeCost)} />
            <div className="border-t border-brand-line mt-1 pt-1">
              <ResultRow label={t("editor.baseCost")} value={fmt(calc.baseCost)} strong />
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle>{t("editor.commercialPrice")}</SectionTitle>
            {calc.vatEnabled && <GuidedTip id="vat" className="mb-4">{t("tips.vat")}</GuidedTip>}
            <ResultRow label={t("editor.netPrice")} value={fmt(calc.recommendedNet)} />
            {calc.vatEnabled && <ResultRow label={`${t("editor.vat")} (${calc.vatRate}%)`} value={fmt(calc.vatAmount)} />}
            <ResultRow label={t("editor.calculatedPrice")} value={fmt(calc.finalPrice)} strong />
            <div className="mt-3 rounded-xl bg-brand-champagne/20 p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-brand-taupe">{t("editor.commercialPrice")}</div>
              <div className="font-serif text-3xl font-semibold text-brand-anthracite">{fmt(calc.commercial.nice)}</div>
              <div className="text-xs text-brand-taupe mt-1">{t("editor.approx")} {fmt(calc.commercial.rounded)} – {fmt(calc.commercial.nice)}</div>
            </div>
          </SectionCard>

          {calc.current && (
            <SectionCard data-testid="editor-comparison">
              <SectionTitle>{t("editor.currentTitle")}</SectionTitle>
              <ResultRow label={t("editor.currentPrice")} value={fmt(calc.current.price)} />
              <ResultRow label={t("editor.currentMargin")} value={`${calc.current.margin.toFixed(0)}%`} tone={calc.current.margin >= calc.margin ? "success" : "danger"} />
              <ResultRow label={t("editor.targetMargin")} value={`${calc.margin.toFixed(0)}%`} />
              <ResultRow label={t("editor.difference")} value={fmt(calc.current.difference)} tone={calc.current.difference >= 0 ? "success" : "danger"} />
            </SectionCard>
          )}

          <SectionCard data-testid="editor-smart">
            <SectionTitle>{t("editor.smartTitle")}</SectionTitle>
            <InfoBox tone={smart.status === "info" ? "neutral" : smart.status}>
              <p>{t(smart.messageKey)}</p>
              {calc.current && smart.status !== "success" && smart.status !== "info" && (
                <p className="mt-2 text-xs">{t("smart.currentMarginLabel")}: {calc.current.margin.toFixed(0)}% · {t("smart.targetMarginLabel")}: {calc.margin.toFixed(0)}%</p>
              )}
              {smart.suggestions.length > 0 && (
                <ul className="mt-3 space-y-1.5 text-sm list-disc list-inside">
                  {smart.suggestions.map((s, i) => (
                    <li key={i} data-testid={`smart-suggestion-${s.type}`}>
                      {s.type === "price" && t("smart.increaseTo", { price: fmt(s.value) })}
                      {s.type === "time" && t("smart.reduceTimeBy", { min: s.value })}
                      {s.type === "material" && t("smart.reduceMaterialBy", { cost: fmt(s.value) })}
                    </li>
                  ))}
                </ul>
              )}
            </InfoBox>
          </SectionCard>

          {fixedTotal > 0 && (
            <SectionCard data-testid="editor-breakeven">
              <SectionTitle>{t("editor.breakEven")}</SectionTitle>
              {be ? (
                <>
                  <p className="text-sm text-brand-anthracite">{t("editor.breakEvenNeed", { n: be, total: fmt(fixedTotal) })}</p>
                  <p className="mt-2 text-xs text-brand-taupe">{t("editor.breakEvenHelp")}</p>
                </>
              ) : (
                <p className="text-sm text-brand-taupe">{t("editor.breakEvenImpossible")}</p>
              )}
            </SectionCard>
          )}

          {!isNew && existing && <WhatIf service={existing} />}
        </div>
      </div>
    </div>
  );
}
